"use client";

import { useEffect, useRef } from "react";
import { readFraction } from "@/lib/analytics/events";

/**
 * A hairline under the reader-tools strip showing how far through the
 * article the reader is — progress through the `data-reader-text` region,
 * measured against the bottom of the viewport, so it reaches the end when the
 * last line has been read, not when it enters the screen. Written straight to
 * the element on scroll (no React state per frame); hidden from assistive
 * tech, which has the reading time and the contents list instead.
 */
export function ReadingProgress() {
  const bar = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const region = document.querySelector<HTMLElement>("[data-reader-text]");
    const el = bar.current;
    if (!region || !el) return;
    let raf = 0;
    const update = () => {
      raf = 0;
      const r = region.getBoundingClientRect();
      el.style.transform = `scaleX(${readFraction(r.top, r.height, window.innerHeight)})`;
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      cancelAnimationFrame(raf);
    };
  }, []);
  return (
    <div aria-hidden className="bg-border/40 absolute inset-x-0 bottom-0 h-0.5">
      <div
        ref={bar}
        className="bg-primary h-full w-full origin-left"
        style={{ transform: "scaleX(0)" }}
      />
    </div>
  );
}
